import { Api, ArrayUtil, DOMUtil, FileUtil, HorizontalAlignment, Logger } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button } from "../../components/button/Button";
import { ButtonVariant } from "../../components/button/ButtonVariant";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { HorizontalSpacer } from "../HorizontalSpacer";
import { Image } from "../../components/image/Image";
import { ImageProps } from "../../components/image/ImageProps";
import { ImageRetrieverInterface } from "./ImageRetrieverInterface";
import { ImageType } from "../../components/image/ImageType";
import { ImageViewerOpenButtons } from "./ImageViewerOpenButtons";
import { ImageViewerValues } from "./ImageViewerValues";
import { Label } from "../../components/label/Label";
import { Layout } from "../../components/layout/Layout";
import { LayoutProps } from "../../components/layout/LayoutProps";
import { MainPageAccessor } from "../../base/MainPageAccessor";
import { OpenType } from "../openButtons/OpenType";
import { Overlay } from "../Overlay";
import { OverlayProps } from "../OverlayProps";
import { Panel } from "../../components/panel/Panel";
import { ReflectiveSnackbars } from "../../base/ReflectiveSnackbars";
import { EmailDialog } from "../dialogs/EmailDialog";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { ImageViewerBehavior } from "./ImageViewerBehavior";

export class ImageViewer extends Layout {
    public static imageRetriever: ImageRetrieverInterface;
    private static logger: Logger;
    private _viewerValues: ImageViewerValues;
    private _buttonClose: Button;
    private _buttonEmail: Button;
    private _buttonDownload: Button;
    private image: Image;
    private _labelTitle: Label;
    private _openButtons: ImageViewerOpenButtons;
    private overlay: Overlay;
    private _panelHeader: Panel;
    private _panelHeaderLeft: Panel;
    private _panelHeaderCenter: Panel;
    private _panelHeaderRight: Panel;
    private _panelPageControls: Panel;
    private _panelZoomControls: Panel;
    private _imageId: string;
    private _fileName: string;

    // LayoutProps have to come first for when we initialize the Layout via the Router
    constructor(props: Partial<LayoutProps>, imageValues?: ImageViewerValues) {
        super({ fillHeight: true, fillRow: true, ...props, needsServerLayout: false });
        this.setProps({ padding: 0, ...props });
        this.add(this.panelHeader);
        this.viewerValues = imageValues;
    }

    override async onLoad() {
        if (this.image?.isEmpty() !== false) {
            await this.lookupImage();
        }

        this.addLayoutLoadListener(() => {
            // If the image viewer is being displayed as the main page in the router, we don't need the close button.
            if (this === ArrayUtil.getFirstElement(MainPageAccessor.router.components)) {
                this.removeCloseButton();
            }
        });
    }

    private set viewerValues(value: ImageViewerValues) {
        this.log.debug("Provided imageValues: %o", value);
        this._viewerValues = value;
        this.createImage(value?.fileName);
        if (this.image) {
            this.image.imageBytes = value?.imageBase64;
        }
        this.syncHeader();
    }

    private get viewerValues(): ImageViewerValues | null {
        return this._viewerValues;
    }

    private syncHeader() {
        this.title = this.viewerValues?.title ?? this.viewerValues?.fileName ?? "Image";
        this.syncHeaderRight();
        this.syncButtons();
        this.syncPageControls();
        this.syncZoomControls();

        if (ImageViewer.behaviors.closeButtonLocation === 'right')
            this.panelHeaderRight.add(this.buttonClose)
        else
            this.panelHeaderLeft.insert(this.buttonClose, 0);
    }

    private async lookupImage() {
        // When opened in a new tab/window, imageId and fileName are populated from URL query parameters by the Router.
        if (this._imageId && this._fileName) {
            this.viewerValues = await ImageViewer.imageRetriever.get(this._imageId, this._fileName);
            delete this._imageId;
            delete this._fileName;
        }
    }

    private syncHeaderRight() {
        if (this.panelHeaderRight) {
            this.panelHeaderRight.removeAll();
            this.panelHeaderRight.add(
                new HorizontalSpacer(),
                ...(this.viewerValues?.displayZoomControls ? [this.panelZoomControls] : []),
                ...(this.viewerValues?.allowEmail ? [this.buttonEmail] : []),
                ...(this.viewerValues?.allowDownloads ? [this.buttonDownload] : []),
                ...(this.viewerValues?.showOpenInButtons ? [...this.openButtons.get()] : [])
            );
        }
    }

    private get panelHeader(): Panel {
        this._panelHeader ??= new Panel({
            fillRow: true,
            themeKey: "dialog.header",
            backgroundColor: "primary.darker",
            color: "primary.reverse",
            rowBreakDefault: false,
            components: [this.panelHeaderLeft, this.panelHeaderCenter, this.panelHeaderRight],
            ...ImageViewer.behaviors.addlHeaderProps
        });
        return this._panelHeader;
    }

    private get panelHeaderLeft(): Panel {
        this._panelHeaderLeft ??= Panel.createNoMarginNoPaddingPanel({
            fillRow: true, rowBreakDefault: false, components: [this.labelTitle]
        });
        return this._panelHeaderLeft;
    }

    private get panelHeaderCenter(): Panel {
        this._panelHeaderCenter ??= Panel.createNoMarginNoPaddingPanel({
            fillRow: true, rowBreakDefault: false, align: HorizontalAlignment.CENTER, components: [this.panelPageControls]
        });
        return this._panelHeaderCenter;
    }

    private get panelHeaderRight(): Panel {
        this._panelHeaderRight ??= Panel.createNoMarginNoPaddingPanel({ fillRow: true, rowBreakDefault: false });
        return this._panelHeaderRight;
    }

    private get labelTitle(): Label {
        this._labelTitle ??= new Label({ paddingRight: 12, fillRow: true, rowBreak: false });
        return this._labelTitle;
    }

    private get buttonEmail(): Button {
        this._buttonEmail ??= new Button({
            variant: ButtonVariant.round,
            tooltip: "Email this image",
            imageName: ImageName.send,
            rowBreak: false,
            onClick: () => this.openEmailDialog()
        });
        return this._buttonEmail;
    }

    private openEmailDialog() {
        const panel = new EmailDialog({
            defaultSubject: `Image: ${this.fileName}`,
            defaultMessage: `Please find the attached image: ${this.fileName}`
        });
        const dialogProps = { yesButtonCaption: "Send", noButtonCaption: "Cancel" };

        ReflectiveDialogs.showYesNo(panel, "Send Email", dialogProps).then(
            async (result) => {
                this.log.debug("Email dialog result: ", result);
                if (result === true) {
                    this.log.debug("Emailing image with values: ", this.viewerValues);
                    try {
                        await Api.post("common/imaging/email-image", {
                            body: panel.messageTextbox.text,
                            cc_user: panel.switchCopyToSelf.checked,
                            file_name: this.fileName,
                            id: this.imageId,
                            recipient: panel.recipientTextbox.text,
                            subject: panel.subjectTextbox.text
                        });
                        ReflectiveSnackbars.showSnackbar(`Emailed ${this.fileName}`);
                    } catch (error: any) {
                        ReflectiveSnackbars.showSnackbar(`Failed to send email: ${error.message}`);
                    }
                }
            }
        );
    }

    private get buttonDownload(): Button {
        this._buttonDownload ??= new Button({
            variant: ButtonVariant.round,
            tooltip: "Download this image",
            imageName: ImageName.download,
            rowBreak: false,
            onClick: () => this.downloadImage()
        });
        return this._buttonDownload;
    }

    private async downloadImage() {
        // If the image being viewed is a TIFF, request it in PDF form and download that file when it's ready.
        // Otherwise, download the file as-is (the bytes we are viewing in the ImageViewer).
        if (this.image.imageType === ImageType.TIFF && ImageViewer.imageRetriever != null) {
            const downloadStartSnackbar = ReflectiveSnackbars.showDownloadSnackbar("Requesting Download",
                "The file will be downloaded when available.", { id: "imageDownloadStart", persist: true });
            const fileNameNoExt = this.fileName.substring(0, this.fileName.lastIndexOf("."));
            let imageViewerValues: ImageViewerValues;
            try {
                imageViewerValues = await ImageViewer.imageRetriever.getAsPdf(this.imageId, fileNameNoExt);
            } catch (error) {
                this.log.error("Unable to get document as PDF, will download document as-is", error);
                imageViewerValues = null;
            }
            if (imageViewerValues != null) {
                FileUtil.downloadBase64AsFile(imageViewerValues.imageBase64, imageViewerValues.fileName);
                downloadStartSnackbar?.dismiss();
                ReflectiveSnackbars.showSnackbar("Downloaded " + imageViewerValues.fileName);
            } else {
                FileUtil.downloadBase64AsFile(this.imageBase64, this.fileName);
                downloadStartSnackbar?.dismiss();
                ReflectiveSnackbars.showSnackbar("Downloaded " + this.fileName);
            }
        } else {
            FileUtil.downloadBase64AsFile(this.imageBase64, this.fileName);
            ReflectiveSnackbars.showSnackbar("Downloaded " + this.fileName);
        }
    }

    private get openButtons(): ImageViewerOpenButtons {
        this._openButtons ??= new ImageViewerOpenButtons(
            this,
            () => this.viewerValues,
            { allowedTypes: [OpenType.NEW_TAB_WITH_SWITCH, OpenType.NEW_WINDOW] }
        );
        return this._openButtons;
    }

    private get buttonClose(): Button {
        this._buttonClose ??= new Button({
            variant: ButtonVariant.round,
            tooltip: "Close this image preview",
            imageName: ImageName.x,
            cancel: true,
            ...ImageViewer.behaviors.addlCloseButtonProps,
            onClick: () => Overlay.hideOverlay(this.overlay)
        });
        return this._buttonClose;
    }

    private removeCloseButton() {
        this.buttonClose.parent?.remove(this.buttonClose);
    }

    private createImage(fileName: string) {
        if (this.image?.imageType !== Image.getImageTypeForFileName(fileName)) {
            this.panelPageControls.removeAll();
            this.panelZoomControls.removeAll();
            if (this.image != null && this.contains(this.image)) {
                this.remove(this.image);
            }
            const imageProps: Partial<ImageProps> = { fillRow: true, fillHeight: true, preserveAspectRatio: true };
            this.image = Image.createFileBasedImage(imageProps, fileName);
            this.add(this.image);
        }
    }

    private syncButtons() {
        const imagePresent = this.image?.isEmpty() === false;
        this.buttonEmail.enabled = imagePresent;
        this.buttonDownload.enabled = imagePresent;
        this.openButtons.enableAll(imagePresent);
    }

    private get panelPageControls(): Panel {
        this._panelPageControls ??= Panel.createNoMarginNoPaddingPanel({ marginRight: 20, rowBreak: false });
        return this._panelPageControls;
    }

    private syncPageControls() {
        this.syncControlsPanel(this.panelPageControls, this.image?.pageControls);
    }

    private get panelZoomControls(): Panel {
        this._panelZoomControls ??= Panel.createNoMarginNoPaddingPanel({ marginRight: 20, rowBreak: false });
        return this._panelZoomControls;
    }

    private syncZoomControls() {
        ImageViewer.behaviors.syncZoomControls(this.image, this.panelZoomControls);
    }

    private syncControlsPanel(controlsPanel: Panel, newControls: Panel) {
        controlsPanel.removeAll();
        if (newControls != null) {
            controlsPanel.add(newControls);
        }
    }

    private set imageId(value: string) {
        this._imageId = value;
    }

    private get imageId(): string {
        return this._imageId ?? this.viewerValues.id;
    }

    private set fileName(value: string) {
        this._fileName = value;
    }

    private get fileName(): string {
        return this._fileName ?? this.viewerValues.fileName;
    }

    private get imageBase64(): string {
        return this.viewerValues.imageBase64;
    }

    public get title(): string {
        return this.labelTitle.text;
    }

    public set title(value: string) {
        this.labelTitle.text = value;
    }

    public showInOverlay(overlayProps: Partial<OverlayProps>, sizeImageToOverlay: boolean = true) {
        this.overlay = Overlay.showInOverlay(this, {
            ...ImageViewer.behaviors.defaultOverlayProps,
            ...overlayProps
        });

        if (sizeImageToOverlay === true) {
            // Ensure the image doesn't overrun the bounds of the overlay
            this.sizeImageToOverlay();
            this.overlay.getOverlayContent().addResizeListener(() => {
                this.sizeImageToOverlay();
            });
        }
    }

    private sizeImageToOverlay() {
        const overlayContentElement = this.overlay.getOverlayContent()._element;
        this.height = DOMUtil.getElementHeightString(overlayContentElement);
        this.width = DOMUtil.getElementWidthString(overlayContentElement);
    }

    public static viewOrDownload(imageViewerValues: ImageViewerValues): void {
        if (ImageViewer.eligibleToView(imageViewerValues.fileName) === true)
            ImageViewer.viewImage(imageViewerValues);
        else {
            FileUtil.downloadBase64AsFile(imageViewerValues.imageBase64, imageViewerValues.fileName);
            ReflectiveSnackbars.showSnackbar("This type of file cannot be previewed; the file has been downloaded as " +
                imageViewerValues.fileName, { persist: true });
        }
    }

    public static eligibleToView(fileName: string) {
        const fileNameLower = fileName.toLowerCase();
        return fileNameLower.endsWith(".jpg") || fileNameLower.endsWith(".jpeg") || fileNameLower.endsWith(".png") ||
            fileNameLower.endsWith(".bmp") || fileNameLower.endsWith(".gif") || fileNameLower.endsWith(".tiff") ||
            fileNameLower.endsWith(".pdf");
    }

    public static viewImage(imageViewerValues: ImageViewerValues, overlayProps?: Partial<OverlayProps>,
        sizeImageToOverlay: boolean = true) {
        if (ImageViewer.eligibleToView(imageViewerValues.fileName) === true) {
            const imageViewer = new ImageViewer({ borderShadow: true }, imageViewerValues);
            imageViewer.showInOverlay(overlayProps, sizeImageToOverlay);
        } else {
            ReflectiveSnackbars.showSnackbar("This type of file cannot be previewed");
        }
    }

    private get log(): Logger {
        if (ImageViewer.logger == null) {
            ImageViewer.logger = DesignableObjectLogManager.getLogger("components.page.imageViewer.ImageViewer");
        }
        return ImageViewer.logger;
    }

    private static get behaviors(): ImageViewerBehavior {
        return ImageViewerBehavior.behaviors;
    }
}
